/**
 * C+D Design System v3.0 — JS token bridge for UPB Viewer Beta
 *
 * Color codes are declared centrally in lib/css/colors.js and re-exported here
 * under design-token names — never hardcode hex in components or this file.
 * Non-color tokens (typography, spacing, radius, shadows, layout) live here.
 *
 * NOTE: Avoid color-mix() — use the pre-computed rgba fallbacks provided here instead.
 */

import {
  upbBetaPrimary,
  upbBetaPrimaryHover,
  upbBetaPrimaryLight,
  upbBetaPrimaryLightRgba,
  upbBetaFg1,
  upbBetaFg2,
  upbBetaFg3,
  upbBetaFg4,
  upbBetaFgInv,
  upbBetaBg1,
  upbBetaBg2,
  upbBetaBg3,
  upbBetaBg4,
  upbBetaBorder1,
  upbBetaBorder2,
  upbBetaCoolGrey,
  upbBetaIconGrey,
  upbBetaButtonStrokeGrey,
  upbBetaStrokeGrey,
  upbBetaHoverGrey,
  upbBetaWhite,
  upbBetaBlack,
  upbBetaSuccess,
  upbBetaWarning,
  upbBetaError,
  upbBetaInfo,
  upbBetaSuccessBg,
  upbBetaWarningBg,
  upbBetaErrorBg,
  upbBetaSuccessLight,
  upbBetaWarningLight,
  upbBetaWhiteEmphasis,
  upbBetaSkeletonBg,
  upbBetaPrimaryLightRgba14,
  upbBetaWhiteOverlay,
  upbBetaErrorText,
  upbBetaSuccessText,
  upbBetaErrorBorder,
  upbBetaSuccessBorder,
  upbBetaInfoBg,
  upbBetaInfoBorder,
  upbBetaInfoText,
  upbBetaPrimaryBorder,
  upbBetaWarningBorder,
  upbBetaWarningText,
  upbBetaChartBarFill,
  upbBetaChartPriceHigh,
  upbBetaChartPriceLow,
  upbBetaChartPriceMed,
  upbBetaChartCursor,
  softPurple,
  softPurpleDark,
  softPurpleLight,
} from 'lib/css/colors';

// ─── Platform primaries ───────────────────────────────────────────────────────
export const PRIMARY_COLOR = upbBetaPrimary; // DP (Distributor Partner) purple
export const PRIMARY_HOVER = upbBetaPrimaryHover;
export const PRIMARY_LIGHT = upbBetaPrimaryLight; // tinted bg on purple surfaces
export const PRIMARY_LIGHT_RGBA = upbBetaPrimaryLightRgba; // color-mix fallback
// Vivid violet accent for the Marco assistant surfaces (brighter than the muted DP purple)
export const ACCENT_VIVID = softPurple; // #8b5cf6
export const ACCENT_VIVID_DARK = softPurpleDark; // #7c3aed
export const ACCENT_VIVID_LIGHT = softPurpleLight; // #ede9fe

// ─── Text / foreground ────────────────────────────────────────────────────────
export const FG_1 = upbBetaFg1; // headlines
export const FG_2 = upbBetaFg2; // body
export const FG_3 = upbBetaFg3; // meta / icons
export const FG_4 = upbBetaFg4; // disabled
export const FG_INV = upbBetaFgInv; // text on dark surfaces

// ─── Backgrounds ─────────────────────────────────────────────────────────────
export const BG_1 = upbBetaBg1; // card / surface
export const BG_2 = upbBetaBg2; // page background
export const BG_3 = upbBetaBg3; // raised / hover
export const BG_4 = upbBetaBg4; // rail / very subtle

// ─── Borders ─────────────────────────────────────────────────────────────────
export const BORDER_1 = upbBetaBorder1; // default borders
export const BORDER_2 = upbBetaBorder2; // subtle borders

// ─── Neutrals ────────────────────────────────────────────────────────────────
export const COOL_GREY = upbBetaCoolGrey;
export const ICON_GREY = upbBetaIconGrey;
export const BUTTON_STROKE_GREY = upbBetaButtonStrokeGrey;
export const STROKE_GREY = upbBetaStrokeGrey;
export const HOVER_GREY = upbBetaHoverGrey;
export const WHITE = upbBetaWhite;
export const BLACK = upbBetaBlack;

// ─── Status ───────────────────────────────────────────────────────────────────
export const SUCCESS_COLOR = upbBetaSuccess;
export const WARNING_COLOR = upbBetaWarning;
export const ERROR_COLOR = upbBetaError;
export const INFO_COLOR = upbBetaInfo;

export const SUCCESS_BG = upbBetaSuccessBg;
export const WARNING_BG = upbBetaWarningBg;
export const ERROR_BG = upbBetaErrorBg;

export const SUCCESS_TEXT = upbBetaSuccessText; // dark text on success_bg
export const ERROR_TEXT = upbBetaErrorText; // dark text on error_bg
export const SUCCESS_BORDER = upbBetaSuccessBorder; // chip/badge border on success_bg
export const ERROR_BORDER = upbBetaErrorBorder; // chip/badge border on error_bg

export const SUCCESS_LIGHT = upbBetaSuccessLight; // success tint (badge bg)
export const WARNING_LIGHT = upbBetaWarningLight; // warning tint (badge bg)
export const INFO_BG = upbBetaInfoBg; // info tint (badge bg)
export const INFO_BORDER = upbBetaInfoBorder; // info tint (badge border)
export const INFO_TEXT = upbBetaInfoText; // dark blue text on info_bg
export const PRIMARY_BORDER = upbBetaPrimaryBorder; // primary tint (badge border)
export const WARNING_BORDER = upbBetaWarningBorder; // warning tint (badge border)
export const WARNING_TEXT = upbBetaWarningText; // dark amber text on warning_bg
export const WHITE_EMPHASIS = upbBetaWhiteEmphasis; // emphasized white text on color
export const SKELETON_BG = upbBetaSkeletonBg; // loading skeleton placeholder
export const PRIMARY_LIGHT_RGBA_14 = upbBetaPrimaryLightRgba14; // focus ring tint
export const WHITE_OVERLAY = upbBetaWhiteOverlay; // translucent white on color fills

// ─── Charts ──────────────────────────────────────────────────────────────────
export const CHART_BAR_FILL = upbBetaChartBarFill; // "units sold" bars
export const CHART_PRICE_HIGH = upbBetaChartPriceHigh; // highest price line
export const CHART_PRICE_LOW = upbBetaChartPriceLow; // lowest price line
export const CHART_PRICE_MED = upbBetaChartPriceMed; // median price line
export const CHART_CURSOR = upbBetaChartCursor; // dashed hover guide

// ─── Typography ──────────────────────────────────────────────────────────────
export const FONT_TITLE = "'Poppins', sans-serif";
export const FONT_BODY = "'Lato', sans-serif";

export const FONT_SIZE_HERO = 32;
export const FONT_SIZE_H1 = 24;
export const FONT_SIZE_H2 = 20;
export const FONT_SIZE_H3 = 18;
export const FONT_SIZE_H4 = 16;
export const FONT_SIZE_BODY = 14;
export const FONT_SIZE_SMALL = 13;
export const FONT_SIZE_XS = 12;
export const FONT_SIZE_XXS = 11;

export const FONT_WEIGHT_REGULAR = 400;
export const FONT_WEIGHT_MEDIUM = 500;
export const FONT_WEIGHT_SEMIBOLD = 600;
export const FONT_WEIGHT_BOLD = 700;
export const FONT_WEIGHT_EXTRABOLD = 800;

// ─── Spacing ─────────────────────────────────────────────────────────────────
export const SPACE_0 = 0;
export const SPACE_1 = 4;
export const SPACE_2 = 8;
export const SPACE_3 = 12;
export const SPACE_4 = 16;
export const SPACE_5 = 24;
export const SPACE_6 = 32;
export const SPACE_7 = 48;
export const SPACE_8 = 64;

// ─── Borders & radius ────────────────────────────────────────────────────────
export const RADIUS_SM = 4;
export const RADIUS_MD = 8;
export const RADIUS_LG = 12;
export const RADIUS_XL = 16;
export const RADIUS_FULL = 9999;

// ─── Elevation shadows ───────────────────────────────────────────────────────
export const SHADOW_XS = '0 1px 2px rgba(16, 24, 40, 0.05)';
export const SHADOW_SM = '0 1px 3px rgba(16,24,40,0.1), 0 1px 2px rgba(16,24,40,0.06)';
export const SHADOW_MD = '0 4px 8px rgba(16,24,40,0.12), 0 2px 4px rgba(16,24,40,0.08)';
export const SHADOW_LG = '0 12px 24px rgba(16,24,40,0.14), 0 4px 8px rgba(16,24,40,0.08)';
export const SHADOW_INSET_SM =
  'inset 0 -1px 0 rgba(0, 0, 0, 0.06), 0 1px 2px rgba(16, 24, 40, 0.06)';

// ─── Layout ───────────────────────────────────────────────────────────────────
export const GLOBAL_NAV_HEIGHT = 40;
export const TOPBAR_HEIGHT = 64;
export const SIDEBAR_WIDTH = 260;
export const KPI_STRIP_HEIGHT = 96;
export const SIDEBAR_CARD_WIDTH = 320;
